
import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Person,
  Email,
  Phone,
  LocationOn,
  Edit,
  Delete,
  Visibility,
} from '@mui/icons-material';
import ManageCustomerDialog from '../components/ManageCustomerDialog';

const mockCustomers = [
  {
    id: 'C001',
    name: 'John Doe',
    email: 'john@example.com',
    phone: '555-123-4567',
    address: '123 Main St, New York, NY 10001',
    idNumber: 'ID123456',
    totalBookings: 12,
    totalSpent: 4500,
    status: 'VIP',
    joinDate: '2023-01-15',
  },
  {
    id: 'C002',
    name: 'Jane Smith',
    email: 'jane@example.com',
    phone: '555-987-6543',
    address: '456 Oak Ave, Los Angeles, CA 90001',
    idNumber: 'ID789012',
    totalBookings: 8,
    totalSpent: 2800,
    status: 'Active',
    joinDate: '2023-03-22',
  },
  {
    id: 'C003',
    name: 'Alice Brown',
    email: 'alice@example.com',
    phone: '555-456-7890',
    address: '789 Pine Rd, Chicago, IL 60007',
    idNumber: 'ID345678',
    totalBookings: 3,
    totalSpent: 900,
    status: 'Active',
    joinDate: '2023-11-05',
  },
  {
    id: 'C004',
    name: 'Bob Wilson',
    email: 'bob@example.com',
    phone: '555-321-0987',
    address: '321 Elm St, Houston, TX 77001',
    idNumber: 'ID901234',
    totalBookings: 15,
    totalSpent: 6200,
    status: 'VIP',
    joinDate: '2022-08-10',
  },
  {
    id: 'C005',
    name: 'Carol Davis',
    email: 'carol@example.com',
    phone: '555-654-3210',
    address: '654 Maple Dr, Miami, FL 33101',
    idNumber: 'ID567890',
    totalBookings: 1,
    totalSpent: 250,
    status: 'Inactive',
    joinDate: '2024-01-20',
  },
];

export default function Customers() {
  const [customers, setCustomers] = React.useState(mockCustomers);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedCustomer, setSelectedCustomer] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [manageDialogOpen, setManageDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState('add');
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const filteredCustomers = customers.filter((customer) => {
    const query = searchQuery.toLowerCase();
    return (
      customer.name.toLowerCase().includes(query) ||
      customer.email.toLowerCase().includes(query) ||
      customer.phone.includes(query) ||
      customer.id.toLowerCase().includes(query)
    );
  });

  const handleMenuClick = (event, customer) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setDialogMode('add');
    setManageDialogOpen(true);
  };

  const handleEditCustomer = () => {
    setDialogMode('edit');
    setManageDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSaveCustomer = (customerData) => {
    if (dialogMode === 'add') {
      setCustomers([...customers, customerData]);
      setSnackbar({
        open: true,
        message: `Customer ${customerData.name} added successfully!`,
        severity: 'success',
      });
    } else {
      setCustomers(customers.map(c => c.id === customerData.id ? customerData : c));
      setSnackbar({
        open: true,
        message: `Customer ${customerData.name} updated successfully!`,
        severity: 'success',
      });
    }
  };

  const handleDeleteCustomer = () => {
    if (selectedCustomer) {
      setCustomers(customers.filter(c => c.id !== selectedCustomer.id));
      setSnackbar({
        open: true,
        message: `Customer ${selectedCustomer.name} removed successfully!`,
        severity: 'success',
      });
      setAnchorEl(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VIP': return 'warning';
      case 'Active': return 'success';
      case 'Inactive': return 'default';
      default: return 'default';
    }
  };

  const stats = [
    { label: 'Total Customers', value: customers.length, color: '#1976d2' },
    { label: 'VIP Customers', value: customers.filter(c => c.status === 'VIP').length, color: '#ed6c02' },
    { label: 'Active Customers', value: customers.filter(c => c.status === 'Active').length, color: '#2e7d32' },
    { 
      label: 'Total Revenue', 
      value: `$${customers.reduce((sum, c) => sum + c.totalSpent, 0).toLocaleString()}`, 
      color: '#9c27b0' 
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Customer Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage customer profiles and booking history
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddCustomer}>
          Add Customer
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                <Typography variant="h4" fontWeight="bold" color={stat.color}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search customers by name, email, phone, or ID..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Customer ID</strong></TableCell>
              <TableCell><strong>Customer Details</strong></TableCell>
              <TableCell><strong>Contact Information</strong></TableCell>
              <TableCell><strong>Bookings</strong></TableCell>
              <TableCell><strong>Total Spent</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCustomers.map((customer) => (
              <TableRow key={customer.id} hover>
                <TableCell><strong>{customer.id}</strong></TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: '#1976d2' }}>
                      {customer.name[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {customer.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Member since {new Date(customer.joinDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="caption">{customer.email}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="caption">{customer.phone}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <strong>{customer.totalBookings}</strong> bookings
                </TableCell>
                <TableCell>
                  <strong>${customer.totalSpent.toLocaleString()}</strong>
                </TableCell>
                <TableCell>
                  <Chip
                    label={customer.status}
                    size="small"
                    color={getStatusColor(customer.status)}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, customer)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleEditCustomer}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit Customer
        </MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleDeleteCustomer}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Remove Customer
        </MenuItem>
      </Menu>

      <ManageCustomerDialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        customer={selectedCustomer}
        onSave={handleSaveCustomer}
        mode={dialogMode}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
